/**
 * Friendsheep gift wrap — pick an eco card, write a note we hand-write for you,
 * add a lokta gift bag.
 *
 * Opens only when someone clicks "Make it a gift" in the cart drawer or on the cart
 * page. Nothing here touches the checkout button.
 *
 * Card and bag markup comes from /cart?view=gift-picker on first open.
 */
(function () {
  'use strict';

  const dialog = document.getElementById('gift-wrap');
  if (!dialog) return;

  const STEPS = [
    {
      key: 'card',
      title: 'Add an Eco card — {price}',
      desc: 'Chosen from partners that give back: every card supports wildlife and permanent reforestation. Printed on 100% recycled paper with eco-friendly inks. Add a handwritten note in the next step.',
      next: 'Add this card',
      skip: 'No card, thank you',
    },
    {
      key: 'note',
      title: 'Add a handwritten note — free',
      desc: 'Write your message here and we will hand-write it into the card for you.',
      next: 'Save my note',
      skip: "No thanks, I'll write my own",
    },
    {
      key: 'bag',
      title: 'Add a gift bag — {price}',
      desc: 'Our lokta paper bags are handmade, hand-printed and completely tree-free.',
      next: 'Add this bag',
      skip: 'No bag, thank you',
    },
  ];

  const titleEl = dialog.querySelector('.gw__title');
  const descEl = dialog.querySelector('[data-gw-desc]');
  const optionsEl = dialog.querySelector('[data-gw-options]');
  const previewEl = dialog.querySelector('[data-gw-preview]');
  const previewNameEl = dialog.querySelector('[data-gw-preview-name]');
  const noteEl = dialog.querySelector('[data-gw-note]');
  const countEl = dialog.querySelector('[data-gw-count]');
  const errorEl = dialog.querySelector('[data-gw-error]');
  const backBtn = dialog.querySelector('[data-gw-back]');
  const skipBtn = dialog.querySelector('[data-gw-skip]');
  const nextBtn = dialog.querySelector('[data-gw-next]');

  const prices = {};
  const selection = { card: null, bag: null };
  let note = '';
  let index = 0;
  let loaded = false;

  /* ---------------------------------------------------------------- options */

  async function loadOptions() {
    if (loaded) return;
    const response = await fetch('/cart?view=gift-picker', { credentials: 'same-origin' });
    optionsEl.innerHTML = await response.text();
    loaded = true;

    optionsEl.querySelectorAll('[data-gw-grid]').forEach(function (grid) {
      prices[grid.dataset.gwGrid] = grid.dataset.price;
    });
    selectDefaults();
    render();
  }

  // Every open starts with the first card and first bag picked, so "Add this card"
  // always has something to add — including a second time round, after a skip.
  function selectDefaults() {
    optionsEl.querySelectorAll('[data-gw-grid]').forEach(function (grid) {
      select(grid.querySelector('.gw-tile'));
    });
  }

  function select(tile) {
    if (!tile) return;
    const grid = tile.closest('[data-gw-grid]');
    const kind = grid.dataset.gwGrid;
    grid.querySelectorAll('.gw-tile').forEach(function (other) {
      other.classList.toggle('is-selected', other === tile);
    });
    selection[kind] = { id: tile.dataset.id, name: tile.dataset.name, image: tile.dataset.full };
    if (STEPS[index].key === kind) showPreview(kind);
  }

  function showPreview(kind) {
    const chosen = selection[kind];
    if (chosen) previewEl.src = chosen.image;
    else previewEl.removeAttribute('src');
    previewEl.alt = chosen ? chosen.name : '';
    previewNameEl.textContent = chosen ? chosen.name : '';
  }

  /* ------------------------------------------------------------------ steps */

  function render() {
    const step = STEPS[index];

    dialog.dataset.step = step.key;
    dialog.dataset.loading = loaded ? 'false' : 'true';
    // Prices land with the fetched grids, so drop the dash entirely until they do.
    const price = prices[step.key];
    titleEl.textContent = price
      ? step.title.replace('{price}', price)
      : step.title.replace(' — {price}', '');
    descEl.textContent = step.desc;

    dialog.querySelectorAll('[data-gw-dot]').forEach(function (dot) {
      dot.classList.toggle('is-active', Number(dot.dataset.gwDot) === index);
    });

    backBtn.hidden = index === 0;
    skipBtn.textContent = step.skip;
    nextBtn.textContent = step.next;
    nextBtn.disabled = !loaded;
    errorEl.hidden = true;

    if (step.key === 'note') noteEl.focus();
    else showPreview(step.key);
  }

  function goTo(next) {
    if (next >= STEPS.length) {
      finish();
      return;
    }
    index = next;
    render();
  }

  function onNext() {
    if (STEPS[index].key === 'note') note = noteEl.value.trim();
    goTo(index + 1);
  }

  function onSkip() {
    const step = STEPS[index];
    if (step.key === 'card') {
      // No card means nothing to hand-write in, so the note step is skipped with it.
      selection.card = null;
      note = '';
      goTo(2);
      return;
    }
    if (step.key === 'note') note = '';
    if (step.key === 'bag') selection.bag = null;
    goTo(index + 1);
  }

  function onBack() {
    // Back out of the bag step lands on the card step when the card was skipped,
    // because the note step was skipped along with it.
    goTo(index === 2 && !selection.card ? 0 : index - 1);
  }

  /* -------------------------------------------------------------- finishing */

  async function finish() {
    const items = [];
    if (selection.card) {
      const item = { id: selection.card.id, quantity: 1 };
      if (note) item.properties = { 'Handwritten message': note };
      items.push(item);
    }
    if (selection.bag) items.push({ id: selection.bag.id, quantity: 1 });

    if (!items.length) {
      dialog.close();
      return;
    }

    nextBtn.disabled = true;
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items }),
    });
    nextBtn.disabled = false;

    // Cards run on single-digit stock, so selling out mid-session is normal, not a
    // fault. Shopify says which one; let them pick again instead of losing the sale.
    if (!response.ok) {
      const error = await response.json();
      errorEl.textContent =
        error.description || 'We could not add that — it may have just sold out.';
      errorEl.hidden = false;
      return;
    }

    dialog.close();

    // The cart page renders its line items server-side; the drawer can refresh in place.
    if (document.getElementById('cart_form')) {
      window.location.reload();
      return;
    }
    const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
    window.globalRefreshCart(cart);
  }

  /* ------------------------------------------------------------------ wiring */

  async function open() {
    index = 0;
    note = '';
    noteEl.value = '';
    countEl.textContent = '0';
    if (loaded) selectDefaults();
    render();
    dialog.showModal();
    await loadOptions();
  }

  optionsEl.addEventListener('click', function (event) {
    const tile = event.target.closest('.gw-tile');
    if (tile) select(tile);
  });

  noteEl.addEventListener('input', function () {
    countEl.textContent = String(noteEl.value.length);
  });

  backBtn.addEventListener('click', onBack);
  skipBtn.addEventListener('click', onSkip);
  nextBtn.addEventListener('click', onNext);

  dialog.querySelector('[data-gw-close]').addEventListener('click', function () {
    dialog.close();
  });

  dialog.addEventListener('click', function (event) {
    if (event.target === dialog) dialog.close();
  });

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-gw-open]') && !dialog.open) open();
  });
})();
